import { FC, useEffect, useMemo } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import { ShopData } from '@shared/api/shop/shop.interface';
import {
  shopOrderListForSellerByShopAccountStateSelectorFamily,
  shopOrderListForSellerByShopNeedAccountStateSelectorFamily,
} from '@shared/api/shopOrder/shopOrder.selector';
import { Button } from '@shared/components/Button';
import { CheckBoxFilter } from '@shared/components/CheckboxFilter';
import { Loading } from '@shared/components/Loading';

import {
  sellerAccountBugoBrandFilterAtom,
  sellerAccountShowAtom,
} from './accountContent.atom';
import { AnnualAccountedContent } from './AnnualAccountedContent';
import { PresentAccountContent } from './PresentAccountContent';

interface Props {
  shop: ShopData;
}

export const SellerAccountContent: FC<Props> = function SellerAccountContent({
  shop,
}: Props) {
  const setSellerAccountBugoBrandFilter = useSetRecoilState(
    sellerAccountBugoBrandFilterAtom,
  );

  const setEnabledHook = useSetRecoilState(sellerAccountShowAtom);

  const shopOrderListState = useRecoilValue(
    shopOrderListForSellerByShopAccountStateSelectorFamily(`${shop._id}`),
  );

  const shopOrderNeedAccountListState = useRecoilValue(
    shopOrderListForSellerByShopNeedAccountStateSelectorFamily(`${shop._id}`),
  );

  // 정산이 필요한 녀석들만 고름
  const needAccountOrder = shopOrderNeedAccountListState?.data;

  useEffect(() => {
    if (shop) {
      const result: CheckBoxFilter = {};
      shop.bugoBrands.forEach(
        (bugoBrand) => (result[bugoBrand?.bugoAgency?.nickName ?? bugoBrand._id] = true),
      );
      result['etc'] = true;
      setSellerAccountBugoBrandFilter(result);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const presentAccount = useMemo(() => {
    if (needAccountOrder && shopOrderNeedAccountListState.status === 'success') {
      return <PresentAccountContent shopOrderList={needAccountOrder ?? []} />;
    } else {
      return <Loading text="정산예정내역 로딩중"></Loading>;
    }
  }, [needAccountOrder, shopOrderNeedAccountListState.status]);

  const annualContent = useMemo(() => {
    if (shopOrderListState?.status === 'success' && shopOrderListState?.data) {
      const shopOrderList = shopOrderListState.data;
      return <AnnualAccountedContent shopOrderList={shopOrderList} />;
    } else if (shopOrderListState?.status === 'loading') {
      return <Loading text="정산완료내역 로딩중"></Loading>;
    } else {
      return (
        <div className="flex items-center justify-end">
          <Button
            onClick={() => {
              setEnabledHook(true);
            }}
          >
            연간 내역 보기
          </Button>
        </div>
      );
    }
  }, [setEnabledHook, shopOrderListState.data, shopOrderListState?.status]);

  return (
    <div className="space-y-4 py-4">
      {presentAccount}
      {annualContent}
    </div>
  );
};
